/**
 * Contact form validation, shared verbatim by the browser and the API.
 *
 * Client-side validation is a convenience; the server runs the exact same
 * rules on every request because a browser check can be bypassed trivially.
 * Keeping one module means the two can never drift apart.
 */

export const REASONS = [
  'Design partner — I have exports to share',
  'A question about something on this site',
  'Technical diligence',
  'Something else',
]

export const LIMITS = {
  name: { min: 2, max: 80 },
  email: { max: 200 },
  company: { max: 120 },
  message: { min: 20, max: 4000 },
}

/** Deliberately permissive: the goal is to catch typos, not to police addresses. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/

/* Matching control characters is the point here: these patterns strip them
   out of user input before it is rendered or copied. */
/* eslint-disable no-control-regex */

/** All C0/C1 control characters. */
const CONTROL_CHARS = new RegExp('[\\u0000-\\u001f\\u007f-\\u009f]', 'g')

/** Control characters except newline, for the free-text message field. */
const CONTROL_CHARS_KEEP_NEWLINE = new RegExp(
  '[\\u0000-\\u0009\\u000b-\\u001f\\u007f-\\u009f]',
  'g',
)
/* eslint-enable no-control-regex */

/**
 * Strips control characters and collapses runs of whitespace. Applied before
 * validation and before anything is stored or forwarded, so a payload can
 * never carry terminal escapes or null bytes into a log.
 */
export function sanitize(value) {
  if (typeof value !== 'string') return ''
  return value
    .replace(CONTROL_CHARS, '')
    .replace(/[ \t]+/g, ' ')
    .trim()
}

/** Same as `sanitize` but preserves paragraph breaks. */
export function sanitizeMultiline(value) {
  if (typeof value !== 'string') return ''
  return value
    .replace(/\r\n/g, '\n')
    .replace(CONTROL_CHARS_KEEP_NEWLINE, '')
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

/**
 * Validates a raw contact payload.
 *
 * @returns {{ valid: boolean, errors: Record<string,string>, data: object }}
 *   `errors` is keyed by field name so each message can be rendered beside the
 *   input it belongs to.
 */
export function validateContact(input) {
  const source = input ?? {}

  const data = {
    name: sanitize(source.name),
    email: sanitize(source.email).toLowerCase(),
    company: sanitize(source.company),
    reason: sanitize(source.reason),
    message: sanitizeMultiline(source.message),
  }

  const errors = {}

  if (!data.name) {
    errors.name = 'Enter your name so we know who we are replying to.'
  } else if (data.name.length < LIMITS.name.min) {
    errors.name = `Your name needs at least ${LIMITS.name.min} characters.`
  } else if (data.name.length > LIMITS.name.max) {
    errors.name = `Your name cannot be longer than ${LIMITS.name.max} characters.`
  }

  if (!data.email) {
    errors.email = 'Enter an email address we can reply to.'
  } else if (data.email.length > LIMITS.email.max) {
    errors.email = `That email address is longer than ${LIMITS.email.max} characters.`
  } else if (!EMAIL_PATTERN.test(data.email)) {
    errors.email = 'That does not look like an email address — check for a typo.'
  }

  if (data.company.length > LIMITS.company.max) {
    errors.company = `Company cannot be longer than ${LIMITS.company.max} characters.`
  }

  if (!data.reason) {
    errors.reason = 'Choose why you are writing.'
  } else if (!REASONS.includes(data.reason)) {
    errors.reason = 'Choose one of the listed reasons.'
  }

  if (!data.message) {
    errors.message = 'Tell us what you have, or what you want to know.'
  } else if (data.message.length < LIMITS.message.min) {
    errors.message = `A useful message is at least ${LIMITS.message.min} characters. Describe the export you hold.`
  } else if (data.message.length > LIMITS.message.max) {
    errors.message = `Keep it under ${LIMITS.message.max} characters — send the detail by reply instead.`
  }

  return { valid: Object.keys(errors).length === 0, errors, data }
}
